// The Unabated Angle §F8 consumption — pure selectors over the
// anonymised crowd-attention index (counts only; never identity).
//
// HONEST DORMANCY: the index itself is gated server-side (a fixture
// only exists once ≥5 distinct watchers; confidence_review at ≥15).
// These selectors never invent attention — an absent fixture ⇒ null,
// an empty index ⇒ empty review list. UI consumers render nothing
// when dormant, exactly like the DEFAULT_SURFACE / honest-empty
// pattern used elsewhere.

export interface FixtureAttention {
  readonly fixture_id: string;
  readonly watchers: number;
  readonly signal_cards_tracked: number;
  readonly attention_score: number;
  readonly confidence_review: boolean;
  readonly detected_at: string;
  readonly updated_at: string;
}

/** The attention row for one fixture, or null when the fixture has
 *  not cleared the server-side dormancy floor (no fabrication). */
export function pickAttention(
  rows: readonly FixtureAttention[],
  fixtureId: string,
): FixtureAttention | null {
  if (!fixtureId) return null;
  return rows.find((r) => r.fixture_id === fixtureId) ?? null;
}

/** Fixtures the crowd signal has flagged for a model confidence
 *  review (confidence_review = true), highest attention first. The
 *  doc's "can trigger model confidence reviews" — surfaced for an
 *  Agent/analyst to act on, never an automatic model change. */
export function reviewFlagged(
  rows: readonly FixtureAttention[],
): FixtureAttention[] {
  return rows
    .filter((r) => r.confidence_review === true)
    .sort((a, b) => b.attention_score - a.attention_score);
}

export type AttentionTier = "review" | "elevated" | "tracked";

/** Coarse, honest tier for display. `review` ⇒ flagged for a
 *  confidence review; `elevated` ⇒ meaningfully watched; `tracked`
 *  ⇒ above the floor but routine. null in ⇒ null out (dormant). */
export function attentionTier(
  row: FixtureAttention | null,
): AttentionTier | null {
  if (!row) return null;
  if (row.confidence_review) return "review";
  if (row.watchers >= 10) return "elevated";
  return "tracked";
}
